// Wire types shared between client and server.
// Keep this file dependency-free so it imports cleanly into both bundles.

export type Phase = 'lobby' | 'countdown' | 'spinning' | 'result';

export type RoomType = 'games' | 'movies' | 'anime';
export const ROOM_TYPES: readonly RoomType[] = ['games', 'movies', 'anime'] as const;

export interface Game {
  id: string;
  name: string;
  // Either logoUrl (catalog) or color (custom user-created tile)
  logoUrl?: string;
  color?: string;
  custom: boolean;
  createdBy?: string;
}

export interface Pick {
  gameId: string;
  weighted: boolean;
}

export interface PublicParticipant {
  id: string;
  name: string;
  color: string;
  picks: Pick[];
  // Game ids this participant has downvoted (×0.5 each, 2+ removes from wheel)
  downvotes: string[];
  ready: boolean;
  connected: boolean;
  vetoUsed: boolean;
  // Used for the dimming/grace UI when disconnected
  disconnectedAt?: number;
}

export interface SpinResult {
  gameId: string;
  // Final wheel rotation in degrees (clients animate to this)
  finalRotation: number;
  durationMs: number;
  startedAt: number;
}

export interface RoomMeta {
  id: string;
  title: string;
  type: RoomType;
  createdAt: number;
  listed: boolean;
}

export interface RoomListing {
  id: string;
  title: string;
  type: RoomType;
  createdAt: number;
  playerCount: number;
}

export interface RoomState {
  meta: RoomMeta;
  phase: Phase;
  participants: PublicParticipant[];
  games: Game[];
  // gameId -> total slice count (after hearts and downvotes)
  weights: Record<string, number>;
  // Slice list in render order (deterministic, sorted by gameId). Excludes
  // games removed by 2+ downvotes or veto.
  slices: Array<{ gameId: string; count: number }>;
  // gameId -> number of distinct connected participants who downvoted it.
  // 1 = halved; 2+ = removed from wheel (grayed out in master list).
  downvoteCounts: Record<string, number>;
  // Vetoes (one per participant) — {gameId, participantId} so the original
  // vetoer can undo it.
  vetoes: Array<{ gameId: string; by: string }>;
  // Countdown deadline (epoch ms) when phase=countdown
  countdownUntil?: number;
  // Current spin
  spin?: SpinResult;
  // Re-spin votes: ids of participants who voted yes; null if no vote in progress
  revote: { proposedBy: string; votes: string[] } | null;
}

// Client -> Server events
export interface ClientToServer {
  // Room lifecycle (replaces the old `hello`).
  createRoom: (payload: { title: string; type: RoomType; listed: boolean }) => void;
  joinRoom: (payload: { code: string; sessionId: string; name?: string }) => void;
  leaveRoom: () => void;
  listRooms: () => void;

  setName: (name: string) => void;
  togglePick: (gameId: string) => void;
  // Toggle the heart on a pick. Each participant can have at most one
  // hearted pick at a time — toggling onto a different pick moves the heart.
  toggleHeart: (gameId: string) => void;
  // Toggle this participant's 50%-downvote on a game.
  toggleDownvote: (gameId: string) => void;
  // Replace the participant's picks with all visible games (true = select all
  // catalog + own customs; false = clear all picks).
  setAllPicks: (selected: boolean) => void;
  addCustomGame: (name: string) => void;
  removeCustomGame: (gameId: string) => void;
  setReady: (ready: boolean) => void;
  veto: () => void;
  unveto: (gameId: string) => void;
  proposeRevote: () => void;
  voteRevote: (yes: boolean) => void;
  acceptResult: () => void;
  adminReset: (token: string) => void;
}

// Server -> Client events
export interface ServerToClient {
  state: (state: RoomState & { youId: string }) => void;
  errorMsg: (msg: string) => void;
  notice: (msg: string) => void;
  roomList: (rooms: RoomListing[]) => void;
  roomCreated: (payload: { code: string }) => void;
}

export const ADMIN_TOKEN_HEADER = 'x-admin-token';
